// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserPlus, Trash2, Shield, Mail } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, Modal, Input, Select, Skeleton, ConfirmDialog } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useUsers, useInviteUser, useUpdateUserRole, useDeleteUser, useResendInvitation } from '@/hooks/useUsers'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { Alert } from '@/components/ui/Alert'

const roleBadgeVariant = {
  owner: 'primary' as const,
  admin: 'info' as const,
  staff: 'ghost' as const,
  // T129-008: accountant role surfaced by the Rails side; use default styling.
  accountant: 'default' as const,
}

export function TeamListPage() {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const { user: currentUser } = useAuth()
  const { data, isLoading, isFetching } = useUsers()
  const inviteUser = useInviteUser()
  const updateRole = useUpdateUserRole()
  const deleteUser = useDeleteUser()
  const resendInvitation = useResendInvitation()
  const addToast = useNotificationStore((s) => s.addToast)
  const formatDate = useDateFormatter()

  // Every assignable role. `owner` is deliberately absent — owners are shown as
  // a badge rather than a dropdown (see the member row below) and are not
  // promoted/demoted from this list. `accountant` (EP-129) belongs here: the API
  // has accepted it since the role shipped (UsersController::VALID_ROLES), but
  // it was missing from this list, so the only way to create one was the console
  // — and an accountant who already existed rendered a BLANK role dropdown,
  // because <Select> falls back to its placeholder when no option matches.
  const roleOptions = [
    { value: 'staff', label: t('roles.staff') },
    { value: 'admin', label: t('roles.admin') },
    { value: 'accountant', label: t('roles.accountant') },
  ]

  const [showInviteModal, setShowInviteModal] = useState(false)
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<{ id: number; name: string } | null>(null)
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'staff',
  })
  const [inviteError, setInviteError] = useState('')

  const isOwner = currentUser?.role === 'owner'
  const members = data?.data || []

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    setInviteError('')

    try {
      await inviteUser.mutateAsync(inviteForm as Parameters<typeof inviteUser.mutateAsync>[0])
      setShowInviteModal(false)
      setInviteForm({ email: '', first_name: '', last_name: '', role: 'staff' })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { errors?: string[] } } }
      setInviteError(error.response?.data?.errors?.join(', ') || tc('errors.something_went_wrong'))
    }
  }

  const handleRoleChange = async (userId: number, role: string) => {
    await updateRole.mutateAsync({ id: userId, role })
  }

  const handleDelete = async () => {
    if (!confirmDeleteUser) return
    await deleteUser.mutateAsync(confirmDeleteUser.id)
    setConfirmDeleteUser(null)
  }

  const handleResendInvitation = async (userId: number) => {
    try {
      await resendInvitation.mutateAsync(userId)
      addToast({ type: 'success', message: t('messages.invite_resent') })
    } catch {
      addToast({ type: 'error', message: tc('errors.something_went_wrong') })
    }
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title'), href: '/settings' }, { label: t('team') }]} />

        <PageHeader
          title={t('team')}
          description={t('team_description')}
          isRefreshing={isFetching && !isLoading}
          actions={
            isOwner ? (
              <Button size="sm" leftIcon={<UserPlus className="w-4 h-4" />} onClick={() => setShowInviteModal(true)}>
                {t('team_section.invite')}
              </Button>
            ) : undefined
          }
        />

        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-1/3 mb-2" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card>
            <div className="divide-y divide-base-200">
              {members.map((member) => {
                const isSelf = member.id === currentUser?.id

                return (
                  <div
                    key={member.id}
                    data-testid={`member-row-${member.email.replace('@', '-at-')}`}
                    // Stacked at phone width: beside the role control the text
                    // column is about 80px wide there, and the note about a
                    // deleted login (EP-150) ran to ten lines.
                    className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-sm font-medium text-primary">
                          {member.first_name.charAt(0)}{member.last_name.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium flex items-center flex-wrap gap-2 break-words">
                          {member.full_name}
                          {isSelf && <span data-testid="self-indicator" className="text-base-content/40 ml-2">{t('team_section.self_indicator')}</span>}
                          {member.pending && (
                            <Badge variant="warning" size="sm">
                              {t('team_section.pending')}
                            </Badge>
                          )}
                          {member.deletion_scheduled_at && (
                            <span data-testid={`deletion-badge-${member.id}`}>
                              <Badge variant="error" size="sm">
                                {t('team_section.login_deleted')}
                              </Badge>
                            </span>
                          )}
                        </p>
                        {/* break-all so long emails wrap within the column instead of
                            overflowing under the role control. */}
                        <p className="text-sm text-base-content/60 break-all">{member.email}</p>
                        {/* EP-150: this person deleted their own login. Only
                            they can restore it, by signing in. */}
                        {member.deletion_scheduled_at && (
                          <p className="text-sm text-error" data-testid={`deletion-note-${member.id}`}>
                            {t('team_section.login_deleted_note', {
                              date: formatDate(member.deletion_scheduled_at),
                            })}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 pl-[3.25rem] sm:pl-0">
                      {/* Owners are shown as a non-editable badge (like self): the
                          role Select only offers admin/staff, so an owner member
                          would otherwise render a blank dropdown, and owners aren't
                          demoted/removed via this list. */}
                      {isOwner && !isSelf && member.role !== 'owner' ? (
                        <>
                          {member.pending && !member.deletion_scheduled_at && (
                            <Button
                              data-testid={`resend-invite-btn-${member.id}`}
                              variant="outline"
                              size="sm"
                              leftIcon={<Mail className="w-4 h-4" />}
                              onClick={() => handleResendInvitation(member.id)}
                              disabled={resendInvitation.isPending && resendInvitation.variables === member.id}
                              loading={resendInvitation.isPending && resendInvitation.variables === member.id}
                              title={t('team_section.resend_invite')}
                            >
                              {t('team_section.resend_invite')}
                            </Button>
                          )}
                          <Select
                            data-testid={`role-select-${member.id}`}
                            options={roleOptions}
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, e.target.value)}
                            size="sm"
                          />
                          <Button
                            data-testid={`delete-btn-${member.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-error"
                            onClick={() => setConfirmDeleteUser({ id: member.id, name: member.full_name })}
                            aria-label={t('team_section.remove_member')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <Badge variant={roleBadgeVariant[member.role]} size="sm">
                          <Shield className="w-3 h-3 mr-1" />
                          {t(`roles.${member.role}`)}
                        </Badge>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        )}

        <Modal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          title={t('team_section.invite')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={() => setShowInviteModal(false)}>
                {tc('actions.cancel')}
              </Button>
              <Button type="submit" form="team-invite-form" loading={inviteUser.isPending}>
                {t('invite.send_invite')}
              </Button>
            </>
          }
        >
          <form id="team-invite-form" onSubmit={handleInvite} className="space-y-4">
            {inviteError && (
              <Alert variant="error" icon={null}>
                <span>{inviteError}</span>
              </Alert>
            )}

            <Input
              label={t('invite.email')}
              name="email"
              type="email"
              placeholder={t('invite.email_placeholder')}
              value={inviteForm.email}
              onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('invite.first_name')}
                name="first_name"
                value={inviteForm.first_name}
                onChange={(e) => setInviteForm((f) => ({ ...f, first_name: e.target.value }))}
                required
              />
              <Input
                label={t('invite.last_name')}
                name="last_name"
                value={inviteForm.last_name}
                onChange={(e) => setInviteForm((f) => ({ ...f, last_name: e.target.value }))}
                required
              />
            </div>

            <Select
              label={t('invite.role')}
              options={roleOptions}
              value={inviteForm.role}
              onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
            />
          </form>
        </Modal>
      </div>

      <ConfirmDialog
        isOpen={confirmDeleteUser !== null}
        onClose={() => setConfirmDeleteUser(null)}
        onConfirm={handleDelete}
        title={t('team_section.remove_member')}
        message={t('messages.remove_confirm', { name: confirmDeleteUser?.name || '' })}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteUser.isPending}
      />
    </>
  )
}
